import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { client } from "queryClient";
import { TaskType } from "types";

interface StopTaskInput {
  taskType: TaskType;
  taskID: string;
  /**
   * true = die Zeile direkt terminalisieren, weil keine Lambda mehr pollt
   * (verwaiste Running-Zeile oder nie angenommene Pending-Zeile).
   */
  forceRelease?: boolean;
}

const requestStop = async ({
  taskType,
  taskID,
  forceRelease,
}: StopTaskInput) => {
  // Zwei verschiedene Vorgänge, die nicht verwechselt werden dürfen:
  //
  // Der kooperative Stopp setzt nur stopRequestedAt. Er setzt voraus, dass eine
  // Lambda das Feld pollt und selbst aussteigt - AWS kann eine laufende
  // Invocation nicht beenden.
  //
  // Genau das ist bei einer verwaisten Zeile aber nicht der Fall: dort pollt
  // niemand mehr, ein stopRequestedAt bliebe wirkungslos und die Oberfläche
  // dauerhaft blockiert. Diese Zeile wird deshalb direkt terminalisiert.
  const input = forceRelease
    ? {
        id: taskID,
        status: "Stopped" as const,
        endTime: new Date().toISOString(),
        errorMessage:
          "Vom Nutzer freigegeben: die Aufgabe hat nicht mehr reagiert.",
      }
    : {
        id: taskID,
        stopRequestedAt: new Date().toISOString(),
      };

  const { errors } =
    taskType === "Calc"
      ? await client.models.CalcTask.update(input)
      : taskType === "Sync"
        ? await client.models.SyncTask.update(input)
        : await client.models.StockTask.update(input);

  if (errors?.length) {
    throw new Error(errors[0].message);
  }

  return taskID;
};

/**
 * Fordert den Abbruch einer laufenden Aufgabe an.
 *
 * Der Zwischenzustand "Stopp angefordert" wird lokal geführt und NICHT aus dem
 * Serverfeld abgeleitet. Grund: AppSync liefert in einem Subscription-Payload nur
 * die Felder, die die auslösende Mutation selektiert hat. Schreibt die Lambda den
 * Status, entscheidet also deren Selection-Set, ob stopRequestedAt mitkommt - ein
 * fehlendes Feld käme als null an und würde den Zustand in der Oberfläche
 * zurücksetzen, obwohl der Abbruch längst vermerkt ist.
 */
export const useTaskStopRequest = () => {
  const [stopRequestedTaskIDs, setStopRequestedTaskIDs] = useState<string[]>(
    [],
  );

  const mutation = useMutation({
    mutationFn: requestStop,
    onSuccess: (taskID) => {
      setStopRequestedTaskIDs((prev) =>
        prev.includes(taskID) ? prev : [...prev, taskID],
      );
    },
  });

  const isStopRequested = useCallback(
    (taskID: string | undefined, serverStopRequestedAt?: string | null) =>
      Boolean(
        (taskID && stopRequestedTaskIDs.includes(taskID)) ||
          serverStopRequestedAt,
      ),
    [stopRequestedTaskIDs],
  );

  return {
    requestStop: mutation.mutateAsync,
    isRequesting: mutation.isPending,
    isStopRequested,
  };
};
